import { useEffect, useState } from 'react';
import { CpmButton, CpmCheckbox, CpmFilterDropdown } from '../../../components/design-system';
import {
    getReportsAdminErrorMessage,
    reportsAdminErrorReason,
    getTemplateDetails,
    listCatalog,
    listStates,
    listTemplateTags,
    updateTemplate,
} from '../../../services/reportsAdminService';
import type { CpmState } from '../../../types/centralProductMaster.types';
import type { ActiveStatus, CatalogListRow, FormPhase } from '../../../types/reportsAdmin.types';
import { REPORTS_COPY, REPORTS_TIPS } from '../copy';
import { canTurnOn, stateName } from '../helpers';
import AppliedFilterChips from '../components/AppliedFilterChips';
import FilterToggleChip from '../components/FilterToggleChip';
import FilteredEmpty from '../components/FilteredEmpty';
import InfoI from '../components/InfoI';
import InPlaceTagToggle from '../components/InPlaceTagToggle';
import ReportTickList from '../components/ReportTickList';
import {
    ReportsAlert,
    ReportsDialog,
    ReportsDialogBody,
    ReportsDialogFooter,
    ReportsDialogHeader,
    ReportsFieldLabel,
    ReportsHelp,
    ReportsInput,
    ReportsSegmented,
    ReportsWait,
    reportsGrid,
    span,
} from '../components/ReportsDialog';
import SearchField from '../components/SearchField';

interface TemplateEditModalProps {
    id: string;
    onClose: () => void;
    onSaved: () => void;
    onApply: () => void;
    stackedOpen?: boolean;
}

export default function TemplateEditModal({
    id,
    onClose,
    onSaved,
    onApply,
    stackedOpen = false,
}: TemplateEditModalProps) {
    const [phase, setPhase] = useState<FormPhase>('loading');
    const [name, setName] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [allStates, setAllStates] = useState(true);
    const [states, setStates] = useState<string[]>([]);
    const [activeStatus, setActiveStatus] = useState<ActiveStatus>('Active');
    const [savedIds, setSavedIds] = useState<string[]>([]);
    const [reportIds, setReportIds] = useState<string[]>([]);
    const [tickRows, setTickRows] = useState<CatalogListRow[]>([]);
    const [alreadyInOnly, setAlreadyInOnly] = useState(true);
    const [tickSearch, setTickSearch] = useState('');
    const [tickStates, setTickStates] = useState<string[]>([]);
    const [tickTouched, setTickTouched] = useState(false);
    const [tagOptions, setTagOptions] = useState<string[]>([]);
    const [stateDocs, setStateDocs] = useState<CpmState[]>([]);
    const [tagsError, setTagsError] = useState(false);
    const [statesError, setStatesError] = useState(false);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [error, setError] = useState('');
    const [justSaved, setJustSaved] = useState(false);

    const locked = phase === 'saving';
    const ticksDirty =
        reportIds.length !== savedIds.length || reportIds.some((item) => !savedIds.includes(item));
    const legalCount = tickRows.filter(
        (row) => reportIds.includes(row.reportID) && canTurnOn(row)
    ).length;
    const canApply =
        !ticksDirty && activeStatus === 'Active' && reportIds.length > 0 && legalCount > 0;

    const load = async () => {
        setPhase('loading');
        setError('');
        try {
            const [details, catalog] = await Promise.all([
                getTemplateDetails(id),
                listCatalog({ templateId: id, limit: 500 }),
            ]);
            setName(details.name);
            setTags(details.tags ?? []);
            setAllStates(Boolean(details.allStates));
            setStates(details.states ?? []);
            setTickStates(details.allStates ? [] : (details.states ?? []));
            setTickTouched(false);
            setActiveStatus(details.activeStatus);
            setSavedIds(details.reportIds ?? []);
            setReportIds(details.reportIds ?? []);
            setTickRows((catalog.reports ?? []).filter((row) => row.kind !== 'ALWAYS_ON'));
            setPhase('idle');
            setOptionsLoading(true);
            setTagsError(false);
            setStatesError(false);
            const [tagsList, stateList] = await Promise.allSettled([
                listTemplateTags(),
                listStates(),
            ]);
            if (tagsList.status === 'fulfilled') setTagOptions(tagsList.value);
            else setTagsError(true);
            if (stateList.status === 'fulfilled') setStateDocs(stateList.value);
            else setStatesError(true);
            setOptionsLoading(false);
        } catch (err) {
            setError(getReportsAdminErrorMessage(err));
            setPhase('fetchError');
        }
    };

    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const persist = async () => {
        if (!name.trim()) {
            setError('Name is required.');
            return;
        }
        setPhase('saving');
        setError('');
        try {
            const row = await updateTemplate({
                id,
                name: name.trim(),
                tags,
                states: allStates ? [] : states,
                allStates,
                activeStatus,
                reportIds,
            });
            setSavedIds(row.reportIds ?? reportIds);
            setJustSaved(true);
            setPhase('idle');
            onSaved();
        } catch (err) {
            setError(getReportsAdminErrorMessage(err));
            setPhase('idle');
        }
    };

    const stateOptions = stateDocs
        .map(stateName)
        .filter(Boolean)
        .map((item) => ({ value: item, label: item }));

    const visibleTicks = tickRows.filter((row) => {
        if (alreadyInOnly && !(reportIds.includes(row.reportID) || row.alreadyIn === true)) {
            return false;
        }
        if (
            tickStates.length &&
            !(row.allStates || tickStates.some((state) => (row.states ?? []).includes(state)))
        ) {
            return false;
        }
        return true;
    });
    const hasTickFilters = Boolean(tickSearch.trim()) || alreadyInOnly || tickStates.length > 0;
    const defaultNote =
        !tickTouched && !allStates && states.length
            ? `This template is tagged for ${states.join(' and ')}, so the list opened filtered to reports for ${
                  states.length === 1 ? 'that state' : 'those states'
              } plus all-states reports. Clear or change it if ops needs the rest of the catalogue.`
            : '';

    return (
        <ReportsDialog
            open
            width={780}
            zIndex={300}
            onClose={locked || stackedOpen ? () => undefined : onClose}
            closeOnBackdrop={!locked && !stackedOpen}
            closeOnEscape={!locked && !stackedOpen}
        >
            <ReportsDialogHeader
                kicker={REPORTS_COPY.TAB_TEMPLATES}
                title={name || REPORTS_COPY.NEW_TEMPLATE}
                subtitle="Template details and the reports in it. Apply uses the saved set, not these ticks."
                onClose={locked || stackedOpen ? undefined : onClose}
            />
            <ReportsDialogBody>
                {phase === 'loading' ? (
                    <div>
                        <div style={{ paddingBottom: 18 }}>
                            <ReportsWait label="Getting this template — its fields and every report in it. Nothing here is a count of zero yet." />
                        </div>
                        <div style={{ ...reportsGrid, gap: '18px 14px' }}>
                            <div style={span(8)}>
                                <div className="cpm-sk" style={{ height: 40 }} />
                            </div>
                            <div style={span(4)}>
                                <div className="cpm-sk" style={{ height: 40 }} />
                            </div>
                            <div style={span(6)}>
                                <div className="cpm-sk" style={{ height: 62 }} />
                            </div>
                            <div style={span(6)}>
                                <div className="cpm-sk" style={{ height: 62 }} />
                            </div>
                            <div style={span(12)}>
                                <div className="cpm-sk" style={{ height: 132 }} />
                            </div>
                        </div>
                    </div>
                ) : null}
                {phase === 'fetchError' ? (
                    <ReportsAlert
                        tone="danger"
                        title="Couldn't load this template"
                        body="Neither the template's fields nor the reports in it came back, so this form is showing nothing rather than guessing. This says nothing about how many reports the template holds. The template list behind this dialog is unaffected, and Catalogue and Store brands were not part of this request."
                        reason={reportsAdminErrorReason(error)}
                        action={
                            <CpmButton variant="outline" size="sm" onClick={() => void load()}>
                                {REPORTS_COPY.RETRY_THIS_TEMPLATE}
                            </CpmButton>
                        }
                    />
                ) : null}
                {phase !== 'loading' && phase !== 'fetchError' ? (
                    <div>
                        {justSaved ? (
                            <div style={{ marginBottom: 16 }}>
                                <ReportsAlert
                                    tone="success"
                                    title={REPORTS_COPY.SAVED}
                                    body="The template was written. No store brand was touched."
                                />
                            </div>
                        ) : null}
                        {error ? (
                            <div style={{ marginBottom: 16 }}>
                                <ReportsAlert
                                    tone="danger"
                                    title="Couldn’t save this template"
                                    body="Nothing was written. Every field and tag you set is still on this form — retry when you are ready. No store brand was touched either way."
                                    reason={reportsAdminErrorReason(
                                        error,
                                        'Nothing was written. Every field and tag you set is still on this form'
                                    )}
                                    action={
                                        <CpmButton
                                            variant="outline"
                                            size="sm"
                                            onClick={() => void persist()}
                                        >
                                            {REPORTS_COPY.RETRY_THE_SAVE}
                                        </CpmButton>
                                    }
                                />
                            </div>
                        ) : null}
                        <div style={reportsGrid}>
                            <div style={span(8)}>
                                <ReportsFieldLabel required>Template name</ReportsFieldLabel>
                                <ReportsInput
                                    value={name}
                                    disabled={locked}
                                    placeholder="What ops calls this template"
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </div>
                            <div style={span(4)}>
                                <ReportsFieldLabel>Template status</ReportsFieldLabel>
                                <ReportsSegmented
                                    value={activeStatus}
                                    disabled={locked}
                                    options={[
                                        { value: 'Active', label: 'Active' },
                                        { value: 'Inactive', label: 'Inactive' },
                                    ]}
                                    onChange={setActiveStatus}
                                />
                            </div>
                            <div style={span(6)}>
                                <ReportsFieldLabel hint="Free text, more than one allowed">
                                    Tags
                                </ReportsFieldLabel>
                                <InPlaceTagToggle
                                    options={tagOptions}
                                    selected={tags}
                                    allowCreate
                                    disabled={locked}
                                    onChange={setTags}
                                />
                                {tagsError ? (
                                    <div style={{ marginTop: 8 }}>
                                        <div
                                            style={{
                                                fontSize: 12.5,
                                                fontWeight: 800,
                                                color: 'var(--cpm-shortfall-text)',
                                            }}
                                        >
                                            {REPORTS_COPY.LOAD_FAIL_TAGS}
                                        </div>
                                        <div
                                            style={{
                                                marginTop: 4,
                                                fontSize: 11.5,
                                                fontWeight: 600,
                                                color: 'var(--cpm-text-secondary)',
                                            }}
                                        >
                                            {REPORTS_COPY.OPTIONS_FAIL_BODY}
                                        </div>
                                        <div style={{ marginTop: 10 }}>
                                            <CpmButton
                                                variant="outline"
                                                size="sm"
                                                onClick={() => void load()}
                                            >
                                                Retry
                                            </CpmButton>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                            <div style={span(6)}>
                                <CpmFilterDropdown
                                    label="States"
                                    portal
                                    disabled={locked}
                                    menuMinWidth={286}
                                    loading={optionsLoading}
                                    loadingLabel={REPORTS_COPY.LOADING_STATES}
                                    failed={statesError}
                                    failedTitle={REPORTS_COPY.LOAD_FAIL_STATES}
                                    failedBody={REPORTS_COPY.OPTIONS_FAIL_BODY}
                                    onRetry={() => void load()}
                                    options={stateOptions}
                                    selected={allStates ? [] : states}
                                    triggerLabel={allStates ? REPORTS_COPY.ALL_STATES : undefined}
                                    onChange={(next) => {
                                        setStates(next);
                                        if (next.length > 0) setAllStates(false);
                                        if (!tickTouched) setTickStates(next);
                                    }}
                                />
                                <div style={{ marginTop: 9 }}>
                                    <CpmCheckbox
                                        checked={allStates}
                                        disabled={locked}
                                        onChange={() =>
                                            setAllStates((v) => {
                                                const next = !v;
                                                if (next) {
                                                    setStates([]);
                                                    if (!tickTouched) setTickStates([]);
                                                } else if (!tickTouched && states.length) {
                                                    setTickStates(states);
                                                }
                                                return next;
                                            })
                                        }
                                        label="All states — any store brand can be given this template"
                                    />
                                </div>
                                <ReportsHelp>
                                    A state-tagged template is offered to store brands in that
                                    state. A state is never a category.
                                </ReportsHelp>
                            </div>
                            <div style={span(12)}>
                                <div
                                    style={{
                                        border: '1px solid var(--cpm-border-default)',
                                        borderRadius: 10,
                                        background: '#fff',
                                        padding: '16px 18px',
                                    }}
                                >
                                    <div
                                        className="flex items-baseline flex-wrap"
                                        style={{ gap: 10 }}
                                    >
                                        <span
                                            className="uppercase"
                                            style={{
                                                fontSize: 9.5,
                                                fontWeight: 800,
                                                letterSpacing: '0.08em',
                                                color: 'var(--cpm-text-tertiary)',
                                            }}
                                        >
                                            Which reports
                                        </span>
                                        <span
                                            style={{
                                                fontSize: 22,
                                                fontWeight: 800,
                                                letterSpacing: '-0.02em',
                                                fontVariantNumeric: 'tabular-nums',
                                            }}
                                        >
                                            {reportIds.length}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 800,
                                                color: 'var(--cpm-text-secondary)',
                                            }}
                                        >
                                            in this template
                                        </span>
                                        <InfoI
                                            title={REPORTS_TIPS.NOT_IN_TEMPLATE.title}
                                            body={REPORTS_TIPS.NOT_IN_TEMPLATE.body}
                                        />
                                    </div>
                                    <div
                                        style={{
                                            marginTop: 5,
                                            maxWidth: 600,
                                            fontSize: 12,
                                            fontWeight: 500,
                                            lineHeight: 1.5,
                                            color: 'var(--cpm-text-secondary)',
                                        }}
                                    >
                                        Tick puts a Shop-can-download report in this template.
                                        Always on cannot go in. Save writes the ticks; Apply uses
                                        the saved set.
                                    </div>
                                    <div
                                        className="flex flex-wrap items-center"
                                        style={{ gap: 9, marginTop: 14 }}
                                    >
                                        <SearchField
                                            value={tickSearch}
                                            onChange={setTickSearch}
                                            placeholder="Search a report by name"
                                            disabled={locked}
                                            width={272}
                                            size="sm"
                                        />
                                        <FilterToggleChip
                                            label={REPORTS_COPY.ALREADY_IN_TEMPLATE}
                                            on={alreadyInOnly}
                                            disabled={locked}
                                            onClick={() => {
                                                setAlreadyInOnly((v) => !v);
                                                setTickTouched(true);
                                            }}
                                        />
                                        <CpmFilterDropdown
                                            label="State"
                                            triggerVariant="chip"
                                            portal
                                            menuMinWidth={286}
                                            disabled={locked}
                                            loading={optionsLoading}
                                            loadingLabel={REPORTS_COPY.LOADING_STATES}
                                            failed={statesError}
                                            failedTitle={REPORTS_COPY.LOAD_FAIL_STATES}
                                            failedBody={REPORTS_COPY.OPTIONS_FAIL_BODY}
                                            onRetry={() => void load()}
                                            options={stateOptions}
                                            selected={tickStates}
                                            triggerLabel={
                                                tickStates.length
                                                    ? `State · ${tickStates.length}`
                                                    : 'State'
                                            }
                                            onChange={(next) => {
                                                setTickStates(next);
                                                setTickTouched(true);
                                            }}
                                        />
                                    </div>
                                    <AppliedFilterChips
                                        chips={[
                                            ...tickStates.map((state) => ({
                                                key: `st-${state}`,
                                                label: state,
                                                onClear: () => {
                                                    setTickStates((prev) =>
                                                        prev.filter((item) => item !== state)
                                                    );
                                                    setTickTouched(true);
                                                },
                                            })),
                                            ...(alreadyInOnly
                                                ? [
                                                      {
                                                          key: 'already',
                                                          label: REPORTS_COPY.ALREADY_IN_TEMPLATE,
                                                          onClear: () => {
                                                              setAlreadyInOnly(false);
                                                              setTickTouched(true);
                                                          },
                                                      },
                                                  ]
                                                : []),
                                            ...(tickSearch.trim()
                                                ? [
                                                      {
                                                          key: 'q',
                                                          label: `Search: ${tickSearch.trim()}`,
                                                          onClear: () => setTickSearch(''),
                                                      },
                                                  ]
                                                : []),
                                        ]}
                                        onClearAll={() => {
                                            setTickSearch('');
                                            setTickStates([]);
                                            setAlreadyInOnly(false);
                                            setTickTouched(true);
                                        }}
                                    />
                                    {defaultNote ? (
                                        <div
                                            style={{
                                                marginTop: 10,
                                                fontSize: 11.5,
                                                fontWeight: 600,
                                                lineHeight: 1.45,
                                                color: 'var(--cpm-text-tertiary)',
                                            }}
                                        >
                                            {defaultNote}
                                        </div>
                                    ) : null}
                                    <div
                                        style={{
                                            margin: '12px 0 8px',
                                            fontSize: 12.5,
                                            fontWeight: 800,
                                            fontVariantNumeric: 'tabular-nums',
                                        }}
                                    >
                                        {hasTickFilters
                                            ? `${visibleTicks.length} of ${tickRows.length} reports match`
                                            : `${tickRows.length} reports in the catalogue`}
                                    </div>
                                    {visibleTicks.length === 0 && hasTickFilters ? (
                                        <FilteredEmpty
                                            title="No report matches what you have on"
                                            cause="The search and filters caused this — the catalogue is not empty, and this is not “this template holds 0 reports”."
                                            restated={`Still on: ${[
                                                alreadyInOnly
                                                    ? REPORTS_COPY.ALREADY_IN_TEMPLATE
                                                    : '',
                                                ...tickStates,
                                                tickSearch.trim()
                                                    ? `Search: ${tickSearch.trim()}`
                                                    : '',
                                            ]
                                                .filter(Boolean)
                                                .join(' · ')}.`}
                                            totalInPlace={`${tickRows.length} reports exist`}
                                            onClear={() => {
                                                setTickSearch('');
                                                setTickStates([]);
                                                setAlreadyInOnly(false);
                                                setTickTouched(true);
                                            }}
                                        />
                                    ) : (
                                        <div style={{ marginTop: 12 }}>
                                            <ReportTickList
                                                rows={visibleTicks}
                                                selectedIds={reportIds}
                                                onToggle={(reportID) => {
                                                    if (locked) return;
                                                    const row = tickRows.find(
                                                        (item) => item.reportID === reportID
                                                    );
                                                    const on = reportIds.includes(reportID);
                                                    if (!on && row && !canTurnOn(row)) return;
                                                    setReportIds((prev) =>
                                                        on
                                                            ? prev.filter(
                                                                  (item) => item !== reportID
                                                              )
                                                            : [...prev, reportID]
                                                    );
                                                }}
                                                disabled={locked}
                                                search={tickSearch}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div
                                    style={{
                                        marginTop: 12,
                                        border: '1px solid var(--cpm-border-default)',
                                        borderRadius: 10,
                                        background: '#fff',
                                        padding: '16px 18px',
                                    }}
                                >
                                    <div
                                        className="flex items-start justify-between flex-wrap"
                                        style={{ gap: 16 }}
                                    >
                                        <div style={{ maxWidth: 560 }}>
                                            <div
                                                className="uppercase"
                                                style={{
                                                    fontSize: 9.5,
                                                    fontWeight: 800,
                                                    letterSpacing: '0.08em',
                                                    color: 'var(--cpm-text-tertiary)',
                                                }}
                                            >
                                                Apply this template to store brands
                                            </div>
                                            <div
                                                style={{
                                                    marginTop: 6,
                                                    fontSize: 13,
                                                    fontWeight: 800,
                                                    letterSpacing: '-0.01em',
                                                }}
                                            >
                                                Turn these reports on for the store brands you pick
                                            </div>
                                            <div
                                                style={{
                                                    marginTop: 4,
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                    lineHeight: 1.5,
                                                    color: 'var(--cpm-text-secondary)',
                                                }}
                                            >
                                                Apply turns the template&apos;s reports on for the
                                                selected store brands and writes only what is
                                                missing. It does not change the template, and the
                                                store brand does not store which template was
                                                applied — adding another report here later turns
                                                nothing on for a store brand applied today.
                                            </div>
                                        </div>
                                        <CpmButton
                                            variant="navy"
                                            size="lg"
                                            disabled={!canApply || locked}
                                            onClick={onApply}
                                        >
                                            {REPORTS_COPY.APPLY_TEMPLATE}
                                        </CpmButton>
                                    </div>
                                    {!canApply ? (
                                        <div style={{ marginTop: 14 }}>
                                            <ReportsAlert
                                                tone="warning"
                                                title={
                                                    ticksDirty
                                                        ? 'Save the ticks before applying'
                                                        : 'This template cannot be applied'
                                                }
                                                body={
                                                    ticksDirty
                                                        ? 'Apply uses the saved set, not a draft. Save first, then apply.'
                                                        : 'Apply is blocked while this template is inactive, empty, or has no legal reports.'
                                                }
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
            </ReportsDialogBody>
            <ReportsDialogFooter
                note="Template details and membership only. Apply is a separate write."
                saving={locked}
                savingLabel="Saving this template. The write has not finished — do not fire it again."
                onCancel={onClose}
                primaryLabel={REPORTS_COPY.SAVE}
                onPrimary={() => void persist()}
                primaryDisabled={phase === 'loading' || phase === 'fetchError' || !name.trim()}
            />
        </ReportsDialog>
    );
}
